import { useAuth } from "@/contexts/AuthContext";
import { postFilteredOrders } from "@/services/Orders";
import { useGetAllSalesItemsByLanguageId, useGetDynamicPropertiesForProduct } from "@/services/Product";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, useMemo } from "react";
import { OrderList } from "@/types";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import FilterPanel from "./FilterPanel";
import DataTable from "@/components/molecules/DataTable";
import SortableHeader from "@/components/molecules/SortableHeader";
import NumberFormatter from "@/components/atoms/NumberFormatter";
import Text from "@/components/atoms/Text";
import Group from "@/components/atoms/Group";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import EmptySearchContent from "@/components/molecules/EmptySearchContent";
import OrderDrawer from "./OrderDrawer";
import styles from "./styles.module.css";

interface MarketListProps {
  onCreateOrder: () => void;
}

const MarketList = ({ onCreateOrder }: MarketListProps) => {
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();

  const [selectedSalesItemId, setSelectedSalesItemId] = useState<number | null>(null);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([]);
  const [selectedOrderTypes, setSelectedOrderTypes] = useState<number[]>([]);
  const [minAmount, setMinAmount] = useState<number | undefined>(undefined);
  const [maxAmount, setMaxAmount] = useState<number | undefined>(undefined);
  const [minPrice, setMinPrice] = useState<number | undefined>(undefined);
  const [maxPrice, setMaxPrice] = useState<number | undefined>(undefined);
  const [limit, setLimit] = useState(15);

  const [orders, setOrders] = useState<OrderList[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState<OrderList | null>(null);

  const { data: salesItems, isLoading: isLoadingSalesItems } = useGetAllSalesItemsByLanguageId(
    user?.language?.id as number,
    {
      enabled: !!user?.language?.id,
    }
  );
  const { data: dynamicProperties, isLoading: isLoadingProperties } = useGetDynamicPropertiesForProduct(
    selectedSalesItemId ?? 0,
    user?.language?.id as number,
    { enabled: !!selectedSalesItemId }
  );

  const { mutateAsync: fetchOrders } = useMutation({
    mutationFn: async (filters: any) => {
      return await postFilteredOrders({
        activeCompanyId: currentCompany?.companyId as number,
        salesItems: [selectedSalesItemId],
        orderType: selectedOrderTypes.length > 0 ? selectedOrderTypes : undefined,
        properties: selectedPropertyIds.length > 0 ? selectedPropertyIds : undefined,
        minimumAmount: minAmount,
        maximumAmount: maxAmount,
        minimumPrice: minPrice,
        maximumPrice: maxPrice,
        pageNumber: currentPage,
        limit: limit,
        contractId: null,
        ...filters,
      });
    },
    onSuccess: (data) => {
      setOrders(data.data.orders || []);
      setTotalPages(data.data.numberOfPages || 0);
      setIsLoadingOrders(false);
    },
    onError: () => {
      setIsLoadingOrders(false);
    },
  });

  const handleSalesItemChange = (salesItemId: number) => {
    if (selectedSalesItemId !== salesItemId) {
      setSelectedSalesItemId(salesItemId);
      setSelectedPropertyIds([]);
    }
  };

  const handlePropertyChange = (propertyId: number) => {
    setSelectedPropertyIds((prev) =>
      prev.includes(propertyId) ? prev.filter((id) => id !== propertyId) : [...prev, propertyId]
    );
  };

  const handleOrderTypeChange = (orderTypeId: number) => {
    setSelectedOrderTypes((prev) =>
      prev.includes(orderTypeId) ? prev.filter((id) => id !== orderTypeId) : [...prev, orderTypeId]
    );
  };

  const handleClearAll = () => {
    setSelectedPropertyIds([]);
    setSelectedOrderTypes([]);
    setMinAmount(undefined);
    setMaxAmount(undefined);
    setMinPrice(undefined);
    setMaxPrice(undefined);
    setCurrentPage(1);
  };

  // Set default selected salesItem to Plastic (or first item if Plastic not found)
  useEffect(() => {
    if (salesItems && salesItems.length > 0 && !selectedSalesItemId) {
      const plasticItem = salesItems.find((item) => item.salesItemTranslated.toLowerCase().includes("plastic"));
      const defaultItem = plasticItem || salesItems[0];
      setSelectedSalesItemId(defaultItem.salesItemId);
    }
  }, [salesItems, selectedSalesItemId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSalesItemId, selectedOrderTypes, selectedPropertyIds, minAmount, maxAmount, minPrice, maxPrice]);

  useEffect(() => {
    if (selectedSalesItemId) {
      setIsLoadingOrders(true);
      fetchOrders({}).then();
    }
  }, [
    selectedSalesItemId,
    selectedOrderTypes,
    selectedPropertyIds,
    minAmount,
    maxAmount,
    minPrice,
    maxPrice,
    currentPage,
    limit,
  ]);

  const staticColumns: ColumnDef<OrderList>[] = useMemo(
    () => [
      {
        header: () => <></>,
        id: "icon",
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          if (row.original.orderType.type === "BUY") {
            return (
              <Group align={"center"} justify={"center"}>
                <SVGAsset name={"purchaseIndicator"} style={{ width: "1.5rem" }} />
              </Group>
            );
          } else if (row.original.orderType.type === "SELL") {
            return (
              <Group align={"center"} justify={"center"}>
                <SVGAsset name={"saleIndicator"} style={{ width: "1.5rem" }} />
              </Group>
            );
          } else {
            return <></>;
          }
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:date")} />,
        id: "created",
        accessorKey: "created",
        cell: ({ row }) => dayjs(row.original.created).format("DD.MM.YYYY"),
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:type")} />,
        id: "salesItem",
        accessorFn: (row) => row.salesItem.salesItem,
        cell: ({ row }) => row.original.salesItem.salesItem,
      },
      {
        header: ({ column }) => <SortableHeader ta="right" column={column} label={t("common:price")} />,
        id: "priceWithFee",
        accessorKey: "priceWithFee",
        cell: ({ row }) => (
          <Text ta="right">
            <NumberFormatter
              value={row.original.priceWithFee?.[`${currentCompany?.companyCurrency?.toUpperCase()}`]?.toFixed(2)} //TODO: handle multiple currencies
              suffix={` ${currentCompany?.companyCurrency?.toUpperCase()}/${row.original.priceUnit.unit}`}
            />
          </Text>
        ),
      },
      {
        header: ({ column }) => <SortableHeader ta="right" column={column} label={t("common:remainingAmount")} />,
        id: "remainingAmount",
        accessorKey: "remainingAmount",
        cell: ({ row }) => (
          <Text ta="right">
            <NumberFormatter value={row.original.remainingAmount?.toFixed(2)} suffix={` ${row.original.unit.unit}`} />
          </Text>
        ),
      },
    ],
    [t, currentCompany]
  );

  const dynamicColumns: ColumnDef<OrderList>[] = useMemo(() => {
    if (!dynamicProperties) return [];

    return dynamicProperties.map((propConfig) => ({
      header: ({ column }) => <SortableHeader column={column} label={propConfig.config.translatedLabel} />,
      id: `property_${propConfig.config.translatedLabel}`,
      accessorFn: (row: OrderList) => {
        const matchingProp = row.properties.find((p) => propConfig.properties.some((cp) => cp.id === p.id));
        return matchingProp?.value ?? "-";
      },
      cell: ({ row }) => {
        const matchingProp = row.original.properties.find((p) => propConfig.properties.some((cp) => cp.id === p.id));
        return matchingProp?.value ?? "-";
      },
    }));
  }, [dynamicProperties, selectedPropertyIds]);

  const allColumns = useMemo(() => {
    return [...staticColumns, ...dynamicColumns];
  }, [staticColumns, dynamicColumns]);

  return (
    <div className={styles.container}>
      <FilterPanel
        salesItems={salesItems}
        isLoadingSalesItems={isLoadingSalesItems}
        selectedSalesItemId={selectedSalesItemId}
        onSalesItemChange={handleSalesItemChange}
        dynamicProperties={dynamicProperties}
        isLoadingProperties={isLoadingProperties}
        selectedPropertyIds={selectedPropertyIds}
        onPropertyChange={handlePropertyChange}
        selectedOrderTypes={selectedOrderTypes}
        onOrderTypeChange={handleOrderTypeChange}
        minAmount={minAmount}
        setMinAmount={setMinAmount}
        maxAmount={maxAmount}
        setMaxAmount={setMaxAmount}
        minPrice={minPrice}
        setMinPrice={setMinPrice}
        maxPrice={maxPrice}
        setMaxPrice={setMaxPrice}
        onClearAll={handleClearAll}
      />

      <div className={styles.tableContainer}>
        {orders.length === 0 && !isLoadingOrders ? (
          <EmptySearchContent onClick={onCreateOrder} />
        ) : (
          <DataTable
            data={orders}
            columns={allColumns}
            isLoading={isLoadingOrders}
            showPagination={true}
            useServerSidePagination={true}
            activePage={currentPage}
            onPageChange={setCurrentPage}
            totalItems={totalPages * 15}
            rowsPerPage={limit}
            onRowsPerPageChange={setLimit}
            onRowClick={(row) => setSelectedOrder(row.original)}
          />
        )}
      </div>

      <OrderDrawer order={selectedOrder} onClose={() => setSelectedOrder(null)} />
    </div>
  );
};

export default MarketList;
